import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, message, Space, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import ytdMisReportService from '../../services/ytdMisReportService';
import { formatFirstName } from '../../helpers/name.helpers';

function formatIndianNumber(value) {
  const num = Number(value);
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number.isFinite(num) ? num : 0);
}

function formatRevenue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '₹0';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs >= 1_00_00_000) {
    return `${sign}₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(abs / 1_00_00_000)} Cr`;
  }
  if (abs >= 1_00_000) {
    return `${sign}₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(abs / 1_00_000)} L`;
  }
  return `${sign}₹${formatIndianNumber(abs)}`;
}

function gapClassName(value) {
  const num = Number(value) || 0;
  if (num > 0) return 'text-green-600';
  if (num < 0) return 'text-red-600';
  return 'text-gray-700';
}

export default function YtdMisReportPage() {
  const [reportDate, setReportDate] = useState(dayjs());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      const data = await ytdMisReportService.getData(dateStr);
      setReport(data);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load YTD MIS report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [reportDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const includedQuarters = report?.included_quarters?.length ? report.included_quarters : [1, 2];
  const currentQuarter = report?.current_quarter ?? includedQuarters[includedQuarters.length - 1];

  const columns = [
    {
      title: 'RM Name',
      dataIndex: 'rm_name',
      key: 'rm_name',
      fixed: 'left',
      width: 160,
      render: (v) => formatFirstName(v),
    },
    ...includedQuarters.flatMap((q) => ([
      {
        title: `Q${q} Target`,
        dataIndex: `q${q}_target`,
        key: `q${q}_target`,
        align: 'right',
        width: 140,
        render: formatRevenue,
      },
      {
        title: `Q${q} Achievement`,
        dataIndex: `q${q}_achievement`,
        key: `q${q}_achievement`,
        align: 'right',
        width: 150,
        render: formatRevenue,
      },
    ])),
    {
      title: 'Overall Gap YTD',
      dataIndex: 'overall_gap_ytd',
      key: 'overall_gap_ytd',
      align: 'right',
      width: 150,
      render: (v) => <span className={gapClassName(v)}>{formatRevenue(v)}</span>,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 140,
      render: formatRevenue,
    },
  ];

  const dataSource = report?.rows ?? [];
  const totals = report?.totals;

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">YTD MIS Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Per-RM YTD revenue targets vs achievement through Q{currentQuarter} (combined Unlisted + Non-Unlisted).
            {report?.fy_label ? ` FY ${report.fy_label}.` : ''}
            {report?.root_user_name ? ` Team under ${report.root_user_name}.` : ''}
          </p>
        </div>
        <Space wrap className="sm:justify-end">
          <DatePicker
            value={reportDate}
            onChange={(v) => v && setReportDate(v)}
            allowClear={false}
          />
          <Button icon={<ReloadOutlined />} onClick={loadReport} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <Table
          rowKey="rm_uuid"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={false}
          scroll={{ x: 400 + includedQuarters.length * 290 }}
          size="middle"
          summary={() => {
            if (!totals) return null;
            let index = 0;
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={index}><strong>Total</strong></Table.Summary.Cell>
                  {includedQuarters.flatMap((q) => {
                    const targetIndex = ++index;
                    const achievementIndex = ++index;
                    return [
                      <Table.Summary.Cell key={`q${q}-target`} index={targetIndex} align="right">
                        <strong>{formatRevenue(totals[`q${q}_target`])}</strong>
                      </Table.Summary.Cell>,
                      <Table.Summary.Cell key={`q${q}-achievement`} index={achievementIndex} align="right">
                        <strong>{formatRevenue(totals[`q${q}_achievement`])}</strong>
                      </Table.Summary.Cell>,
                    ];
                  })}
                  <Table.Summary.Cell index={++index} align="right">
                    <strong className={gapClassName(totals.overall_gap_ytd)}>
                      {formatRevenue(totals.overall_gap_ytd)}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={++index} align="right">
                    <strong>{formatRevenue(totals.revenue)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </div>
    </DashboardPageWrapper>
  );
}
